/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { messageSummaryStyles } from './themes/MessageStyles.css';
import { MessageVariant } from './Message.types';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';

type AriaProps = Pick<UnpackSignals<JSX.AriaAttributes>, 'aria-level'>;

/**
 * Props for the Summary component
 */
type Props = AriaProps &
  TestIdProps & {
    /**
     * The role associated with the message summary.
     * In MessageToast and MessageBanner, role should be 'heading' and aria-level should be 2.
     * In ComponentMessage, role should be undefined.
     */
    role?: UnpackSignals<JSX.HTMLAttributes['role']>;

    /**
     * The summary text
     */
    text?: string;

    /**
     * Style variant
     */
    variant?: MessageVariant;
  };

/**
 * Summary Component for rendering the summary text of the Message
 */
function MessageSummary({
  'aria-level': ariaLevel,
  role,
  text,
  testId,
  variant = 'banner'
}: Props) {
  const testIdProps = useTestId(testId);

  // If detail content is rendered, then wrap it in a div with specified style classes
  // TODO:  Refactor to utilize useComponentTheme and multiVariantStyles.
  const classes = classNames([messageSummaryStyles.base, messageSummaryStyles[variant]]);
  return (
    <div aria-level={ariaLevel} role={role} class={classes} {...testIdProps}>
      {text}
    </div>
  );
}

export { MessageSummary };
